import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ArrowRight, Search } from 'lucide-react';
import API_URL from '../config/API_URL';

export const SelectBookModal = ({ isOpen, onClose, onSelectBook }) => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 12,
    total_pages: 1,
    total_books: 0,
    has_next: false,
    has_prev: false
  });

  useEffect(() => {
    if (isOpen) {
      fetchBooks(1, 12);
    }
  }, [isOpen]);

  const fetchBooks = async (page = 1, perPage = 12) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/books?page=${page}&per_page=${perPage}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch books');
      }
      const data = await response.json();
      setBooks(data.books || []);
      setPagination(data.pagination || {
        page: 1,
        per_page: perPage,
        total_pages: 1,
        total_books: 0,
        has_next: false,
        has_prev: false
      });
    } catch (error) {
      console.error('Error fetching books:', error);
      setBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    fetchBooks(newPage, pagination.per_page);
  };

  const filteredBooks = books.filter(book =>
    book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    book.author.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '1rem',
        maxWidth: '1000px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(90deg, #9333ea 0%, #7e22ce 100%)',
          color: 'white'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
            Select a Book to Record
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <X size={24} />
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={20}
              style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }}
            />
            <input
              type="text"
              placeholder="Search books by title or author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 3rem',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#9333ea'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            />
          </div>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.5rem'
        }}>
          {loading ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
              Loading books...
            </p>
          ) : filteredBooks.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
              {searchQuery ? 'No books match your search.' : 'No books available yet.'}
            </p>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '1.5rem'
            }}>
              {filteredBooks.map(book => (
                <div
                  key={book.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.75rem',
                    padding: '1.25rem',
                    textAlign: 'center',
                    background: 'white',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1)';
                  }}
                >
                  <div>
                    {book.pdf_url ? (
                      <div style={{
                        width: '100%',
                        height: '200px',
                        marginBottom: '0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        overflow: 'hidden',
                        backgroundColor: '#f9fafb'
                      }}>
                        <iframe
                          src={`${book.pdf_url.trim()}#page=1&view=FitH`}
                          style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            pointerEvents: 'none'
                          }}
                          title={`${book.title} preview`}
                        />
                      </div>
                    ) : (
                      <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>📖</div>
                    )}
                    <h4 style={{
                      fontWeight: '600',
                      color: '#1f2937',
                      fontSize: '1.125rem',
                      margin: 0,
                      marginBottom: '0.25rem'
                    }}>
                      {book.title}
                    </h4>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      margin: 0
                    }}>
                      by {book.author}
                    </p>
                    {book.description && (
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#9ca3af',
                        marginTop: '0.5rem',
                        lineHeight: '1.4'
                      }}>
                        {book.description.length > 80
                          ? `${book.description.substring(0, 80)}...`
                          : book.description}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => onSelectBook(book)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#9333ea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#7e22ce'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#9333ea'}
                  >
                    Select Book <ArrowRight size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Pagination */}
        {!loading && filteredBooks.length > 0 && !searchQuery && (
          <div style={{
            padding: '1.5rem',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
              {Math.min(pagination.page * pagination.per_page, pagination.total_books)} of{' '}
              {pagination.total_books} books
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.has_prev}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db',
                  background: pagination.has_prev ? 'white' : '#f3f4f6',
                  color: pagination.has_prev ? '#374151' : '#9ca3af',
                  cursor: pagination.has_prev ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.875rem'
                }}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
                  .filter(pageNum => {
                    return pageNum === 1 ||
                           pageNum === pagination.total_pages ||
                           Math.abs(pageNum - pagination.page) <= 1;
                  })
                  .map((pageNum, index, array) => {
                    const showEllipsis = index > 0 && pageNum - array[index - 1] > 1;
                    return (
                      <React.Fragment key={pageNum}>
                        {showEllipsis && (
                          <span style={{ padding: '0.5rem', color: '#9ca3af' }}>...</span>
                        )}
                        <button
                          onClick={() => handlePageChange(pageNum)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            borderRadius: '0.375rem',
                            border: '1px solid #d1d5db',
                            background: pageNum === pagination.page ? '#9333ea' : 'white',
                            color: pageNum === pagination.page ? 'white' : '#374151',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: pageNum === pagination.page ? 'bold' : 'normal',
                            minWidth: '2.5rem'
                          }}
                        >
                          {pageNum}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db',
                  background: pagination.has_next ? 'white' : '#f3f4f6',
                  color: pagination.has_next ? '#374151' : '#9ca3af',
                  cursor: pagination.has_next ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.875rem'
                }}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};